import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/modules/auth/store/use-auth"
import transformUserInfo from "../../utils/transform-user-info"
import useLoginByCode from "../../queries/use-login-by-code"
import { isClientError } from "@educabot/fetcher"

interface LoginByCodeProps {
  code: string
}

export default function LoginByCode({ code }: LoginByCodeProps) {
  const { isPending, isError, data, isSuccess, mutateAsync } = useLoginByCode()
  const { logIn, partialToken, setPartialToken } = useAuth()
  const [isCoursesError, setIsCoursesError] = useState(false)

  useEffect(() => {
    handleLoginByCode()
  }, [])

  useEffect(() => {
    if (isSuccess && data) {
      const user = transformUserInfo({
        ...data.data.description.user,
        userRole: data.data.description.user.role,
      })

      logIn({ token: partialToken, user })
      setPartialToken("")
    }
  }, [isSuccess, data])

  const handleLoginByCode = async () => {
    try {
      mutateAsync({
        body: { code },
        token: partialToken,
      })
    } catch (error) {
      if (!isClientError(error)) return
      const errorCode = error.code
      if (errorCode === "U10") {
        setIsCoursesError(true)
      }
    }
  }

  return (
    <div className="flex size-full flex-col items-center justify-center gap-3">
      {isPending ? (
        <p>Cargando...</p>
      ) : isError && !isCoursesError ? (
        <>
          <p>Error al cargar el usuario. Por favor, vuelve a intentar.</p>
          <Button
            onClick={() => {
              mutateAsync({
                body: { code },
                token: partialToken,
              })
            }}
          >
            Reintentar
          </Button>
        </>
      ) : (
        <div className="w-full max-w-[560px]">
          <div className="w-full pb-5">
            <div className="mx-auto flex size-[160px] items-center justify-center rounded-full bg-primary-100 text-center">
              <span className="text-[80px]">🎓</span>
            </div>
            <p className="my-4 text-center text-2xl font-bold">
              No podés acceder a TUNI, por ahora.
            </p>
            <p className="text-center text-lg text-gray-500">
              Lamentablemente, todavía no estás asignado a un curso que te permita usar la
              plataforma.
            </p>
          </div>
          <div className="border-t border-t-neutral-200 pt-5">
            <h6 className="text-center text-2xl font-bold text-primary-500">¿Qué es TUNI?</h6>
            <p className="my-4 text-center text-sm">
              Es la herramienta que va a acompañarte para poder aprobar tus materias 💪
            </p>
            <div className="flex w-full flex-wrap items-center justify-center gap-4">
              <div className="flex shrink-0 items-center justify-center gap-1 rounded-lg bg-primary-100 px-2 py-1 text-black">
                <span>🗓️</span>
                <span className="text-sm">Planificar tu cursada a tu ritmo</span>
              </div>

              <div className="flex shrink-0 items-center justify-center gap-1 rounded-lg bg-primary-100 px-2 py-1 text-black">
                <span>📚</span>
                <span className="text-sm">Organizar tus tiempos de estudio</span>
              </div>

              <div className="flex shrink-0 items-center justify-center gap-1 rounded-lg bg-primary-100 px-2 py-1 text-black">
                <span>📝</span>
                <span className="text-sm">Hacer actividades sobre los temas de tus materias</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
